import EastRoundedIcon from "@mui/icons-material/EastRounded";
import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK, NEUTRAL, STAFF_PICKER } from "@/theme/tokens";
import { ShiftWindowIcon } from "@/ui/Icons/ShiftWindowIcon";
import SystemButton from "@/ui/SystemButton/SystemButton";
import { InHouseTooltip } from "../InHouseTooltip/InHouseTooltip";
import type { AddStaffSummary, ShiftDetail } from "./addStaffUtils";
import { ShiftDetailsChips } from "./ShiftDetailsChips";

type Props = {
	detail: ShiftDetail;
	onDetailChange: (detail: ShiftDetail) => void;
	summary: AddStaffSummary;
	/** Day (0) / night (1) slot of the picked window, for the summary's sun/moon; `null` before a pick. */
	shiftIndex: number | null;
	hasSelection: boolean;
	/** The picks, in pick order — named in a tooltip on "3 staff" so the count is checkable. */
	selectedStaff: readonly SelectedStaff[];
	/** "Adds $480 this week" for wage viewers picking several; `null` hides it. Already translated. */
	costLabel: string | null;
	confirmLabel: string;
	canSubmit: boolean;
	/** The confirm button spins: the add is publishing, or waiting on the picked window's predicted cost. */
	confirmLoading: boolean;
	onCancel: () => void;
	onConfirm: () => void;
};

/** One pick as the footer names it — keyed by the nurse (Add Staff) or by the shift (Remove Staff lists one row per shift). */
export type SelectedStaff = { id: number | string; name: string };

const Separator: FC = () => (
	<Box component="span" aria-hidden sx={{ color: NEUTRAL[450] }}>
		·
	</Box>
);

type WhoLabelProps = {
	who: string;
	hasSelection: boolean;
	selectedStaff: readonly SelectedStaff[];
	/** Prefix of the `-who` / `-names` test ids (`add-nurse-summary`, `remove-staff-summary`). */
	testIdPrefix: string;
};

/**
 * "3 staff", with the names behind it on hover / focus — a count alone can't be checked before the action.
 * Shared by the Add Staff and Remove Staff footers' read-backs.
 */
export const WhoLabel: FC<WhoLabelProps> = ({ who, hasSelection, selectedStaff, testIdPrefix }) => {
	const label = (
		<Box component="span" sx={{ fontWeight: 600, color: hasSelection ? INK.PRIMARY : INK.MUTED }}>
			{who}
		</Box>
	);
	if (selectedStaff.length < 2) return label;
	return (
		<InHouseTooltip
			placement="top"
			arrow
			title={
				<Box component="ul" data-testid={`${testIdPrefix}-names`} sx={{ m: 0, pl: 2, fontSize: FONT_SIZE.SECONDARY }}>
					{selectedStaff.map(({ id, name }) => (
						<li key={id}>{name}</li>
					))}
				</Box>
			}
		>
			<Box
				component="span"
				tabIndex={0}
				data-testid={`${testIdPrefix}-who`}
				sx={{
					textDecoration: "underline dotted",
					textUnderlineOffset: `${STAFF_PICKER.SUMMARY_UNDERLINE_OFFSET}px`,
					textDecorationColor: NEUTRAL[450],
					cursor: "default",
					borderRadius: STAFF_PICKER.SUMMARY_FOCUS_RADIUS,
				}}
			>
				{label}
			</Box>
		</InHouseTooltip>
	);
};

type SummaryLineProps = {
	summary: AddStaffSummary;
	shiftIndex: number | null;
	hasSelection: boolean;
	selectedStaff: readonly SelectedStaff[];
	costLabel: string | null;
};

/** "Karyn Thomas → Tue, Sep 22 ☀ 3:00 PM–11:00 PM · East Unit" — what the button is about to do. */
const SummaryLine: FC<SummaryLineProps> = ({ summary, shiftIndex, hasSelection, selectedStaff, costLabel }) => (
	<Box
		data-testid="add-nurse-summary"
		aria-live="polite"
		sx={{
			display: "flex",
			alignItems: "center",
			flexWrap: "wrap",
			columnGap: 1,
			rowGap: 0.25,
			minWidth: 0,
			flex: 1,
			fontSize: FONT_SIZE.EMPHASIS,
			color: INK.SECONDARY,
		}}
	>
		<WhoLabel who={summary.who} hasSelection={hasSelection} selectedStaff={selectedStaff} testIdPrefix="add-nurse-summary" />
		<EastRoundedIcon aria-hidden sx={{ fontSize: FONT_SIZE.EMPHASIS, color: NEUTRAL[450] }} />
		<span>{summary.date}</span>
		{summary.hours && shiftIndex !== null && (
			<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, whiteSpace: "nowrap" }}>
				<ShiftWindowIcon shiftIndex={shiftIndex} labelled />
				<Box component="span" sx={{ fontWeight: 600, color: INK.PRIMARY }}>
					{summary.hours}
				</Box>
			</Box>
		)}
		{summary.location && (
			<>
				<Separator />
				<span>{summary.location}</span>
			</>
		)}
		{costLabel && (
			<>
				<Separator />
				<Box
					component="span"
					data-testid="add-nurse-summary-cost"
					sx={{ fontWeight: 600, color: INK.PRIMARY, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
				>
					{costLabel}
				</Box>
			</>
		)}
	</Box>
);

/**
 * The footer row wraps: "Shift details" first, then the read-back + buttons as one group — beside the chips
 * while that group still gets `ADD_ACTION_GROUP_MIN_WIDTH`, on its own row under them on a narrower panel.
 * A short screen tightens the padding, so the staff list above keeps its rows.
 */
const FOOTER_SX = {
	display: "flex",
	alignItems: "center",
	flexWrap: "wrap",
	columnGap: 3,
	rowGap: 1.75,
	px: 3,
	pt: 2,
	pb: 2.5,
	borderTop: `1px solid ${NEUTRAL[200]}`,
	[STAFF_PICKER.COMPACT_VIEWPORT_QUERY]: { rowGap: 1, pt: 1.25, pb: 1.25 },
} as const;

/**
 * The panel's footer: the "Shift details" chips, then a read-back of the add and its buttons. The
 * summary is the confirmation — it says who goes where before the button does it.
 */
export const AddStaffFooter: FC<Props> = ({
	detail,
	onDetailChange,
	summary,
	shiftIndex,
	hasSelection,
	selectedStaff,
	costLabel,
	confirmLabel,
	canSubmit,
	confirmLoading,
	onCancel,
	onConfirm,
}) => {
	const { t } = useTranslation();
	return (
		<Box component="footer" sx={FOOTER_SX} data-testid="add-nurse-footer">
			<Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
				<Typography component="span" sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 500, color: INK.MUTED }}>
					{t("add.staff.details")}
				</Typography>
				<ShiftDetailsChips value={detail} onChange={onDetailChange} />
			</Box>
			<Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5, flex: `1 1 ${STAFF_PICKER.ADD_ACTION_GROUP_MIN_WIDTH}px`, minWidth: 0 }}>
				<SummaryLine summary={summary} shiftIndex={shiftIndex} hasSelection={hasSelection} selectedStaff={selectedStaff} costLabel={costLabel} />
				<Box sx={{ display: "flex", alignItems: "center", gap: 1, marginInlineStart: "auto" }}>
					<SystemButton data-testid="add-nurse-cancel-button" buttonVariant="borderless" size="medium" onClick={onCancel} noFullWidthMobile>
						{t("cancel")}
					</SystemButton>
					<SystemButton
						data-testid="add-nurse-confirm-button"
						size="medium"
						onClick={onConfirm}
						disabled={!canSubmit}
						loading={confirmLoading}
						noFullWidthMobile
					>
						{confirmLabel}
					</SystemButton>
				</Box>
			</Box>
		</Box>
	);
};
